import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { type ConversationStarter } from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
import {
	type AssistanceServiceInjectedParams,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useUIConfig } from '../../common/utils/ui-config';
import { useWindowLocation } from '../../common/utils/use-window-location';

import messages from './messages';

export type ConversationStarterWithLabel = ConversationStarter & {
	labelType?: ConversationStarterLabelType;
	llmLabel?: { message: MessageDescriptor };
};

export enum PageContextType {
	ADMIN_HUB = 'admin-hub',
	BITBUCKET = 'bitbucket',
	BITBUCKET_PULL_REQUEST = 'bitbucket-pull-request',
	BITBUCKET_REPOSITORY_SOURCE = 'bitbucket-repository-source',
	CONFLUENCE_HOME = 'confluence-home',
	CONFLUENCE_PAGE_VIEW = 'confluence-page-view',
	CONFLUENCE_PAGE_EDIT = 'confluence-page-edit',
	CONFLUENCE_WHITEBOARD = 'confluence-whiteboard',
	JIRA_HOME = 'jira-home',
	JIRA_ISSUE_VIEW = 'jira-issue-view',
	JIRA_PROJECT = 'jira-project',
	ATLAS_HOME = 'atlas-home',
	UNKNOWN = 'unknown',
}

export type ConversationStarterLabelType =
	| 'be-productive'
	| 'stay-up-to-date'
	| 'summarize-content'
	| 'rephrase-content'
	| 'draft-content'
	| 'find-information'
	| 'find-people'
	| 'measure-productivity'
	| 'get-answers'
	| 'llm-generated';

const PAGE_TYPE_CONVERSATION_STARTERS: {
	[key in PageContextType]: Array<{
		labelType: ConversationStarterLabelType;
		message: MessageDescriptor;
	}>;
} = {
	[PageContextType.BITBUCKET_PULL_REQUEST]: [
		{ labelType: 'summarize-content', message: messages.bitbucketPullRequestsPrompt1 },
		{ labelType: 'rephrase-content', message: messages.bitbucketPullRequestsPrompt2 },
		{ labelType: 'summarize-content', message: messages.bitbucketPullRequestsPrompt3 },
	],
	[PageContextType.BITBUCKET_REPOSITORY_SOURCE]: [
		{ labelType: 'summarize-content', message: messages.bitbucketRepositorySourcePrompt1 },
		{ labelType: 'find-information', message: messages.bitbucketRepositorySourcePrompt2 },
		{ labelType: 'find-information', message: messages.bitbucketRepositorySourcePrompt3 },
	],
	[PageContextType.BITBUCKET]: [
		{ labelType: 'find-information', message: messages.bitbucketPrompt1 },
		{ labelType: 'find-information', message: messages.bitbucketPrompt4 },
		{ labelType: 'stay-up-to-date', message: messages.bitbucketPrompt3 },
	],
	[PageContextType.CONFLUENCE_HOME]: [
		{ labelType: 'be-productive', message: messages.confluenceHomePrompt1 },
		{ labelType: 'stay-up-to-date', message: messages.confluenceHomePrompt2 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt3 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt4 },
		{ labelType: 'rephrase-content', message: messages.confluenceHomePrompt5 },
		{ labelType: 'draft-content', message: messages.confluenceHomePrompt6 },
		{ labelType: 'find-information', message: messages.confluenceHomePrompt7 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt8 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt9 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt10 },
		{ labelType: 'be-productive', message: messages.confluenceHomePrompt11 },
	],
	[PageContextType.CONFLUENCE_PAGE_VIEW]: [
		{ labelType: 'summarize-content', message: messages.confluencePageViewPrompt1 },
		{ labelType: 'summarize-content', message: messages.confluencePageViewPrompt2 },
		{ labelType: 'rephrase-content', message: messages.confluencePageViewPrompt3 },
		{ labelType: 'find-people', message: messages.confluencePageViewPrompt4 },
		{ labelType: 'stay-up-to-date', message: messages.confluencePageViewPrompt5 },
		{ labelType: 'be-productive', message: messages.confluencePageViewPrompt6 },
	],
	[PageContextType.CONFLUENCE_WHITEBOARD]: [
		{ labelType: 'summarize-content', message: messages.confluenceWhiteboardPrompt1 },
		{ labelType: 'get-answers', message: messages.confluenceWhiteboardPrompt2 },
		{ labelType: 'find-information', message: messages.confluenceWhiteboardPrompt3 },
		{ labelType: 'be-productive', message: messages.confluenceWhiteboardPrompt4 },
	],
	[PageContextType.CONFLUENCE_PAGE_EDIT]: [
		{ labelType: 'rephrase-content', message: messages.confluencePageEditPrompt1 },
		{ labelType: 'draft-content', message: messages.confluencePageEditPrompt2 },
		{ labelType: 'find-information', message: messages.confluencePageEditPrompt3 },
		{ labelType: 'be-productive', message: messages.confluencePageEditPrompt4 },
	],
	[PageContextType.JIRA_HOME]: [
		{
			labelType: 'stay-up-to-date',
			message: messages.jiraHomePrompt1,
		},
		{
			labelType: 'be-productive',
			message: messages.jiraHomePrompt2,
		},
		{
			labelType: 'find-information',
			message: messages.jiraHomePrompt3,
		},
		{
			labelType: 'summarize-content',
			message: messages.jiraHomePrompt4,
		},
		{ labelType: 'draft-content', message: messages.jiraHomePrompt5 },
		{ labelType: 'be-productive', message: messages.jiraHomePrompt6 },
	],
	[PageContextType.JIRA_ISSUE_VIEW]: [
		{
			labelType: 'find-information',
			message: messages.jiraIssueViewPrompt1,
		},
		{ labelType: 'rephrase-content', message: messages.jiraIssueViewPrompt2 },
		{
			labelType: 'draft-content',
			message: messages.jiraIssueViewPrompt3,
		},
		{ labelType: 'be-productive', message: messages.jiraIssueViewPrompt4 },
	],
	[PageContextType.JIRA_PROJECT]: [
		{ labelType: 'stay-up-to-date', message: messages.jiraProjectPrompt1 },
		{
			labelType: 'be-productive',
			message: messages.jiraProjectPrompt2,
		},
		{
			labelType: 'measure-productivity',
			message: messages.jiraProjectPrompt3,
		},
		{
			labelType: 'find-people',
			message: messages.jiraProjectPrompt4,
		},
		{
			labelType: 'find-people',
			message: messages.jiraProjectPrompt5,
		},
		{
			labelType: 'be-productive',
			message: messages.jiraProjectPrompt6,
		},
		{
			labelType: 'measure-productivity',
			message: messages.jiraProjectPrompt7,
		},
		{ labelType: 'find-people', message: messages.jiraProjectPrompt8 },
		{
			labelType: 'find-information',
			message: messages.jiraProjectPrompt9,
		},
		{
			labelType: 'get-answers',
			message: messages.jiraProjectPrompt10,
		},
		{
			labelType: 'rephrase-content',
			message: messages.jiraProjectPrompt11,
		},
		{
			labelType: 'summarize-content',
			message: messages.jiraProjectPrompt12,
		},
		{
			labelType: 'draft-content',
			message: messages.jiraProjectPrompt13,
		},
		{ labelType: 'be-productive', message: messages.jiraProjectPrompt14 },
	],
	[PageContextType.ATLAS_HOME]: [
		{ labelType: 'find-information', message: messages.atlasHomePrompt1 },
		{ labelType: 'measure-productivity', message: messages.atlasHomePrompt2 },
		{ labelType: 'get-answers', message: messages.atlasHomePrompt3 },
		{ labelType: 'draft-content', message: messages.atlasHomePrompt4 },
		{ labelType: 'be-productive', message: messages.atlasHomePrompt5 },
		{ labelType: 'draft-content', message: messages.atlasHomePrompt6 },
		{ labelType: 'be-productive', message: messages.confluenceHomePrompt1 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt4 },
		{ labelType: 'rephrase-content', message: messages.confluenceHomePrompt5 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt11 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt8 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt10 },
		{
			labelType: 'stay-up-to-date',
			message: messages.jiraHomePrompt2,
		},
		{
			labelType: 'find-information',
			message: messages.jiraHomePrompt3,
		},
		{
			labelType: 'summarize-content',
			message: messages.jiraHomePrompt4,
		},
		{ labelType: 'be-productive', message: messages.atlasHomePrompt7 },
	],
	[PageContextType.UNKNOWN]: [
		{ labelType: 'find-information', message: messages.unknownPrompt1 },
		{ labelType: 'be-productive', message: messages.unknownPrompt2 },
		{ labelType: 'draft-content', message: messages.unknownPrompt3 },
	],
	[PageContextType.ADMIN_HUB]: [],
};

export const LLM_PAGE_TYPE_LABELS: {
	[key in PageContextType]?: Array<{
		message: MessageDescriptor;
	}>;
} = {
	[PageContextType.CONFLUENCE_HOME]: [
		{ message: messages.confluenceHomeLLMLabel1 },
		{ message: messages.confluenceHomeLLMLabel2 },
		{ message: messages.confluenceHomeLLMLabel3 },
	],
	[PageContextType.CONFLUENCE_PAGE_VIEW]: [
		{ message: messages.confluencePageLLMLabel1 },
		{ message: messages.confluencePageLLMLabel2 },
		{ message: messages.confluencePageLLMLabel3 },
		{ message: messages.confluencePageLLMLabel4 },
	],
	[PageContextType.CONFLUENCE_WHITEBOARD]: [
		{ message: messages.confluenceWhiteboardLLMLabel1 },
		{ message: messages.confluenceWhiteboardLLMLabel2 },
		{ message: messages.confluenceWhiteboardLLMLabel3 },
	],
	[PageContextType.CONFLUENCE_PAGE_EDIT]: [
		{ message: messages.confluencePageLLMLabel1 },
		{ message: messages.confluencePageLLMLabel2 },
		{ message: messages.confluencePageLLMLabel3 },
		{ message: messages.confluencePageLLMLabel4 },
	],
	[PageContextType.JIRA_HOME]: [
		{ message: messages.jiraProjectLLMLabel1 },
		{ message: messages.jiraProjectLLMLabel2 },
		{ message: messages.jiraProjectLLMLabel3 },
		{ message: messages.jiraProjectLLMLabel4 },
	],
	[PageContextType.JIRA_ISSUE_VIEW]: [
		{
			message: messages.jiraIssueLLMLabel1,
		},
		{
			message: messages.jiraIssueLLMLabel2,
		},
		{
			message: messages.jiraIssueLLMLabel3,
		},
		{
			message: messages.jiraIssueLLMLabel4,
		},
	],
	[PageContextType.JIRA_PROJECT]: [
		{ message: messages.jiraProjectLLMLabel1 },
		{ message: messages.jiraProjectLLMLabel2 },
		{ message: messages.jiraProjectLLMLabel3 },
		{ message: messages.jiraProjectLLMLabel4 },
	],
	[PageContextType.UNKNOWN]: [{ message: messages.unknownLLMLabel1 }],
};

export const determinePageType = (location: string): PageContextType => {
	const urlPatterns: [PageContextType, RegExp][] = [
		[PageContextType.BITBUCKET_REPOSITORY_SOURCE, /bitbucket\.org\/[^\/]+\/[^\/]+\/src\//],
		[PageContextType.BITBUCKET_PULL_REQUEST, /bitbucket\.org\/[^\/]+\/[^\/]+\/pull-requests\//],
		[PageContextType.BITBUCKET, /bitbucket\.org\/.*/],
		[PageContextType.CONFLUENCE_HOME, /\/wiki\/home$/],
		[PageContextType.CONFLUENCE_PAGE_EDIT, /\/wiki\/spaces\/[^\/]+\/(?:pages|blog)\/edit-v2\//],
		[PageContextType.CONFLUENCE_PAGE_VIEW, /\/wiki\/spaces\/[^\/]+\/(?:pages|blog)\//],
		[PageContextType.CONFLUENCE_WHITEBOARD, /\/wiki\/spaces\/[^\/]+\/whiteboard\//],
		[PageContextType.JIRA_ISSUE_VIEW, /\/browse\/[^\/]+$/],
		[PageContextType.JIRA_ISSUE_VIEW, /\/jira\/.+\/projects\/.+\?selectedIssue/],
		[PageContextType.JIRA_PROJECT, /\/jira\/.+\/projects\//],
		[PageContextType.JIRA_HOME, /\/jira\/dashboards\/last-visited$/],
		// atlas home is either home.atlassian.com, home.stg.atlassian.com, team.atlassian.com, or team.stg.atlassian.com
		[PageContextType.ATLAS_HOME, /\/(home|team)\./],
		// admin hub: admin.atlassian.com (production) or admin.stg.atlassian.com (staging)
		[PageContextType.ADMIN_HUB, /admin(\.stg)?\.atlassian\.com/],
	];

	for (const [type, pattern] of urlPatterns) {
		const matched = pattern.test(location);
		if (!matched) {
			continue;
		}

		if (type === PageContextType.CONFLUENCE_WHITEBOARD) {
			const isExperimentEnable = FeatureGates.getExperimentValue(
				'contextual_conversation_starters_for_whiteboards',
				'enable',
				false,
			);

			return isExperimentEnable ? type : PageContextType.UNKNOWN;
		}

		return type;
	}

	return PageContextType.UNKNOWN;
};

export const usePageContextType = () => {
	const location = useWindowLocation();
	const [pageContextType, setPageContextType] = useState<PageContextType>(
		determinePageType(location),
	);

	useEffect(() => {
		setPageContextType(determinePageType(location));
	}, [location]);

	return pageContextType;
};

const shuffleArray = <T>(arr: T[]): T[] => {
	// Shuffle array using Fisher-Yates algorithm
	for (let i = arr.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[arr[i], arr[j]] = [arr[j], arr[i]];
	}

	return arr;
};

const getRandomStarters = (starters: ConversationStarterWithLabel[], num: number) => {
	// Return the first `num` items
	return shuffleArray(starters).slice(0, num);
};

export const llmGeneratedConvoStartersPageTypeAllowList = [
	PageContextType.CONFLUENCE_PAGE_VIEW,
	PageContextType.CONFLUENCE_PAGE_EDIT,
	PageContextType.CONFLUENCE_WHITEBOARD,
	PageContextType.JIRA_ISSUE_VIEW,
];

const getConversationStarterLimit = (isFullscreen: boolean) => (isFullscreen ? 6 : 3);

export const useGetLLMLabelWithoutRepeat = (pageContextType: PageContextType) => {
	/**
	 * To avoid repetition, this function shuffles the labels array
	 * and for every function call, it always get the next one in the shuffled array.
	 *
	 * Using `useMemo`, this function will reset the labelsShuffled when pageContextType changes.
	 * so it will always randomise the correct labels for the current pageContextType.
	 */
	const getLLMLabel = useMemo(() => {
		const labelsByPageType = LLM_PAGE_TYPE_LABELS[pageContextType];
		if (!labelsByPageType) {
			return () => ({ labelText: undefined, currentLabelIndex: -1 });
		}

		const shuffledLabels = shuffleArray(labelsByPageType);
		let labelIndex = 0;

		return () => {
			const currentLabelIndex = labelIndex;
			const labelText = shuffledLabels[labelIndex];
			// make sure the index loops back to the beginning of the array
			labelIndex = (labelIndex + 1) % shuffledLabels.length;

			return { labelText, currentLabelIndex };
		};
	}, [pageContextType]);

	return {
		getLLMLabel,
	};
};

// THIS WILL BE DEPRECATED ONCE ALL USERS ARE MIGRATED TO CHAT-REFRESH-V2 (platform/packages/ai-mate/rovo-conversation-actions/src/controllers/rovo-conversation-starters/index.tsx)
export const useContextualConversationStarters = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
		onError,
		onSuccess,
		onInit,
	}: AssistanceServiceInjectedParams & {
		onInit?: () => void;
		onSuccess?: () => void;
		onError?: (error: ApiError) => void;
	}) => {
		const { formatMessage } = useIntl();
		const pageContextType = usePageContextType();
		const { fullScreen } = useUIConfig();

		const [didTimeOut, setDidTimeOut] = useState(false);
		const [starters, setStarters] = useState<Array<ConversationStarterWithLabel | null>>(
			new Array(3).fill(null),
		);
		const timeoutRef = useRef<number | null>(null);

		const cancelTimeout = useCallback(() => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		}, []);

		const staticContextualConversationStarters = useMemo(() => {
			const contextualStarters: ConversationStarterWithLabel[] = PAGE_TYPE_CONVERSATION_STARTERS[
				pageContextType
			].map((starter) => {
				return {
					labelType: starter.labelType,
					message: formatMessage(starter.message),
					type: 'static',
				};
			});

			const conversationStarterLimit = getConversationStarterLimit(fullScreen ?? false);

			// We don't need to shuffle the starters when the page context type is unknown
			const staticContextualStarters =
				pageContextType === PageContextType.UNKNOWN
					? contextualStarters.slice(0, conversationStarterLimit)
					: getRandomStarters(contextualStarters, conversationStarterLimit);
			return staticContextualStarters;
		}, [pageContextType, fullScreen, formatMessage]);

		const { getLLMLabel } = useGetLLMLabelWithoutRepeat(pageContextType);

		const {
			mutate: getDynamicContextualStarters,
			error,
			isSubmitting,
		} = useMutation({
			mutationFn: async ({
				fallbackStarters,
				userDefinedStarters = [],
				locationUrl: browser_url,
			}: {
				fallbackStarters: Array<ConversationStarterWithLabel>;
				userDefinedStarters?: Array<ConversationStarterWithLabel>;
				locationUrl: string;
			}) => {
				onInit?.();

				experienceTracker.start({
					id: ExperienceName.FETCH_CONTEXTUAL_STARTERS,
					name: ExperienceName.FETCH_CONTEXTUAL_STARTERS,
				});
				setDidTimeOut(false);
				setStarters(
					[
						...userDefinedStarters,
						...fallbackStarters.slice(0, 1),
						...new Array(3).fill(null),
					].slice(0, 3),
				);
				beginTimeout(userDefinedStarters, fallbackStarters);
				return await assistanceService.generateContextualStarters({ browser_url });
			},
			onSuccess: async (responseData) => {
				onSuccess?.();

				experienceTracker.succeed({
					name: ExperienceName.FETCH_CONTEXTUAL_STARTERS,
				});

				let updatedStarters = [...starters];

				if (didTimeOut) {
					return;
				}
				cancelTimeout();

				responseData?.conversation_starters.map(({ text: responseStarter }) => {
					const index = updatedStarters.indexOf(null);
					const starterAlreadyExists = updatedStarters.some(
						(starter) => starter?.message === responseStarter,
					);

					// If there is a null value in the starters array AND the new starter we get from the backend
					// does not already exist in the array, replace the null value with the new starter
					if (index !== -1 && !starterAlreadyExists) {
						updatedStarters[index] = {
							message: responseStarter,
							type: 'llm-generated',
							labelType: 'llm-generated',
							llmLabel: getLLMLabel().labelText,
						};

						setStarters(updatedStarters);
					}
				});
			},
			onError: (error) => {
				onError?.(error);

				experienceTracker.fail({
					name: ExperienceName.FETCH_CONTEXTUAL_STARTERS,
					error,
				});
			},
		});

		const beginTimeout = useCallback(
			(
				userDefinedStarters: Array<ConversationStarterWithLabel>,
				fallbackStarters: Array<ConversationStarterWithLabel>,
			) => {
				timeoutRef.current = window.setTimeout(() => {
					experienceTracker.abort({
						name: ExperienceName.FETCH_CONTEXTUAL_STARTERS,
						reason: 'timeout',
					});
					setDidTimeOut(true);
					const conversationStarterLimit = getConversationStarterLimit(fullScreen ?? false);
					setStarters([...userDefinedStarters, ...fallbackStarters].slice(0, conversationStarterLimit));
				}, 2000);

				return cancelTimeout;
			},
			[cancelTimeout, experienceTracker, fullScreen],
		);

		return {
			getDynamicContextualStarters,
			starters,
			error,
			// TODO - Implement appropriate request abort logic for useMutation, much like useStreamingMutation has, this is a stop gap
			isMutating: didTimeOut ? false : isSubmitting,
			staticContextualConversationStarters,
			cancelTimeout,
		};
	},
);
